import { z } from "zod";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Scan Unsent Invoices
 *
 * Finds every open invoice with no send event recorded — neither a Gently
 * email send nor a Famous-side send event has flipped `isSent`, so these
 * invoices were created but never delivered to the customer — and queues them
 * all into a single AR task. The task's action opens the bulk invoice send
 * dialog so the user can email each one to the customer contact on file in
 * one pass.
 *
 * `invoiceMinAgeHours` gives freshly created invoices a grace period before
 * they count as "unsent"; `lookbackDays` bounds the sweep so ancient history
 * doesn't flood the task. Invoices with a zero (or credit) remaining balance,
 * or with any unpriced line item on the backing sales order, are skipped —
 * those aren't ready to send.
 *
 * The task is a per-organization singleton: a re-scan refreshes the open
 * task's invoice list in place, and a scan that comes back empty cancels a
 * still-pending task so the dashboard never shows stale work.
 */
export const scanUnsentInvoicesStep = defineStepConfig({
  type: "scan_unsent_invoices",
  name: "Scan Unsent Invoices",
  description:
    "Find open invoices with a positive balance and fully priced line items " +
    "that were created but never sent, and queue them into a single bulk " +
    "invoice send task.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    taskId: z
      .string()
      .nullable()
      .describe("Id of the created/refreshed task; null when no work found."),
    taskCreated: z
      .boolean()
      .describe(
        "True when a new task was created; false when an existing open task " +
          "was refreshed or no task was needed.",
      ),
    invoiceCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of unsent invoices queued for sending."),
    totalBalanceDue: z
      .string()
      .describe(
        "Total remaining balance (decimal string) across the queued invoices.",
      ),
  }),

  configSchema: z.object({
    invoiceMinAgeHours: z
      .number()
      .int()
      .positive()
      .default(24)
      .describe(
        "Grace period before an invoice counts as unsent, measured on the " +
          "aging-basis date. Basis dates are date-only, so this rounds up " +
          "to whole days.",
      ),
    lookbackDays: z
      .number()
      .int()
      .positive()
      .default(90)
      .describe(
        "Only include invoices whose aging-basis date falls within this " +
          "many days, so the sweep doesn't dredge up ancient history.",
      ),
    agingBasis: z
      .enum(["days_overdue", "days_since_invoice", "days_since_shipment"])
      .default("days_since_invoice")
      .describe(
        "Which date the age windows are measured from: since due date " +
          "(days_overdue), since invoice date, or since ship date.",
      ),
    assigneeUserId: z
      .uuid()
      .optional()
      .describe(
        "User the generated task is assigned to. Leave empty to create it " +
          "unassigned (it still shows on the AR summary dashboard).",
      ),
    taskPriority: z
      .enum(["low", "medium", "high", "urgent"])
      .default("medium")
      .describe("Priority of the generated task."),
  }),

  emits: [SystemEvent.TASK_CREATED] as const,

  execution: {
    startToCloseTimeout: "120 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
